import { z } from "zod";

export const ParticipantRole = z.enum(["teacher", "student"]);
export type ParticipantRole = z.infer<typeof ParticipantRole>;

/**
 * Идентификатор урока. Он же имя комнаты в LiveKit.
 *
 * ⚠️ **Имя Y.Doc перестанет быть синонимом id урока с групповой комнатой**
 * (`docs/group-room-design.md`, раздел 4): у занятия группы N уроков и один
 * общий документ, названный сеансом. Считает имя `boardIdOf` ниже, и звать надо
 * её, а не подставлять `lesson.id`, даже пока ответы у них совпадают.
 *
 * Схема сама по себе от этого не изменилась: id сеанса тоже uuid, и `safeParse`
 * пропускает его молча. Именно поэтому гейты имени документа в `apps/collab`
 * собраны в одну функцию (`isLessonDocument`): ослаблять её надо осознанно и в
 * одном месте, а не четырьмя одинаковыми строками в обработчиках.
 */
export const LessonId = z.string().uuid();
export type LessonId = z.infer<typeof LessonId>;

/**
 * Имя доски урока: имя его Y.Doc и ключ, которым адресуется вся комната.
 *
 * ⚠️ **Это единственное место, где решается, общая доска или своя.** У урока без
 * сеанса имя это его собственный id, и никакого «а вдруг» тут нет: правило 1
 * раздела 5.3 CLAUDE.md требует один документ на урок с ротацией по границе
 * урока, и у занятия 1:1 эта граница и есть урок. У урока с сеансом имя это id
 * сеанса, то есть документ один на всё занятие группы, а граница ротации
 * по-прежнему занятие, а не полугодовая тетрадь: правило не нарушено.
 *
 * Функция чистая и живёт в общем пакете, потому что имя считают ОБЕ стороны:
 * сервер (тикет, снимок, уборка документа) и клиент доски. Разъехавшись, они
 * дали бы ученику подключение к документу, которого никто не пишет.
 */
export function boardIdOf(lesson: { id: string; groupSessionId: string | null }): string {
  return lesson.groupSessionId ?? lesson.id;
}

/**
 * Имя документа, в котором лежат ОТВЕТЫ этого ученика.
 *
 * ⚠️ **Правило одно и оно короче, чем кажется: ответы всегда живут в документе,
 * названном УРОКОМ.** У занятия 1:1 урок и доска это одно и то же имя, поэтому
 * документ там по-прежнему один и не меняется ни байтом. У занятия группы доска
 * названа сеансом, а урок у каждого свой, и разница имён и есть разделение
 * ответов.
 *
 * Почему не ключ по ученику в общем документе (`answers[slide][ex][userId]`),
 * хотя обвязка там дешевле: Yjs синхронизирует документ ЦЕЛИКОМ, значит у
 * каждого ребёнка в браузере лежали бы ответы всех остальных, и «скрыто» стало
 * бы свойством интерфейса, а не данных. Разбор целиком в
 * `docs/group-lessons-design.md`, раздел 4.
 *
 * ⚠️ **Функция существует ради читаемости вызова, а не ради вычисления.** Она
 * возвращает `lesson.id` всегда, и соблазн подставить его строкой велик. Не
 * надо: рядом стоит `boardIdOf`, который тоже иногда равен `lesson.id`, и место
 * вызова обязано говорить, ЧЕГО оно просит, доску или ответы. Пара таких мест
 * уже существует на клиенте, на сервере и в снимке.
 */
export function answersDocIdOf(lesson: { id: string }): string {
  return lesson.id;
}

/**
 * Что участнику разрешено на доске: писать или только смотреть.
 *
 * Живёт здесь, а не рядом с подписью тикета (`@edu/shared/node`), потому что это
 * правило комнаты, а не свойство формата пропуска. Форматов у пропуска может
 * стать два, правило от этого не меняется.
 */
export const BoardScope = z.enum(["rw", "r"]);
export type BoardScope = z.infer<typeof BoardScope>;

/**
 * Кому в этой комнате разрешено писать.
 *
 * Правило одно и оно короткое: **ученик В СЕАНСЕ смотрит, все остальные пишут**
 * (`docs/group-room-design.md`, раздел 7). Разворачивать его в «преподаватель
 * пишет, ученик 1:1 пишет, ученик группы смотрит» нельзя: три ветки означают три
 * места, где однажды забудут четвёртую.
 *
 * ⚠️ **Ученик 1:1 по-прежнему `rw`, и это не оговорка, а половина смысла.** Доска
 * пары общая, ученик на ней решает и пишет, и отнять это значило бы сломать
 * сегодняшний урок ради занятия, которого ещё ни у кого нет.
 *
 * ⚠️ **Область это ПЕРВЫЙ из двух рубежей, и сам по себе он человеку не виден.**
 * Сервер на readOnly-соединении апдейт молча роняет, `synced` при этом остаётся
 * истинным, полосы обрыва нет. Второй рубеж (`viewModeEnabled` холста и
 * `readonly` панели упражнений) снимается клиентом с ответа на ЭТОТ тикет, см.
 * `board-scope.ts` у web.
 */
export function boardScopeOf(who: {
  role: ParticipantRole;
  /** Сеанс комнаты. `null` это занятие 1:1. */
  sessionId: string | null;
}): BoardScope {
  return who.role === "student" && who.sessionId !== null ? "r" : "rw";
}

/**
 * Опорный урок комнаты: наименьший по id живой урок. `null`, если живых нет.
 *
 * Им адресуется вход преподавателя, донор копии материала, выбор заготовки и
 * сверка отпечатка доски. Нужен он потому, что уроков в комнате N, а спросить
 * («какая заготовка у этого занятия?») надо один раз и получить один и тот же
 * ответ у всех участников и на всех перезапросах.
 *
 * ⚠️ **Правило выбора обязано быть УСТОЙЧИВЫМ, а не просто однозначным.**
 * «Первый вошедший» и «алфавитно первый ученик» оба меняются в течение занятия:
 * первый зависит от порядка входа, второй от состава группы. Наименьший id не
 * зависит ни от чего, кроме набора живых уроков.
 *
 * ⚠️ **Считается ТОЛЬКО по живым**, независимо от того, с каким `deleted`
 * спрашивали уроки комнаты. Отсюда второе свойство, на котором держится уборка
 * документа: `null` здесь и есть ответ «живых уроков в комнате не осталось»,
 * второго запроса для этого не нужно.
 *
 * Сравнение строковое, и это не срезание угла: uuid у нас записаны нижним
 * регистром с дефисами на фиксированных местах, поэтому лексикографический
 * порядок совпадает с порядком `ORDER BY id` по типу `uuid` в Postgres.
 */
export function baseLessonOf(live: readonly string[]): string | null {
  let best: string | null = null;
  for (const id of live) {
    if (best === null || id < best) best = id;
  }
  return best;
}

/**
 * Сколько человек помещается в комнату звонка, считая преподавателя.
 *
 * **Одно число на всю систему** (решение владельца 01.09.2026): не четыре у
 * занятия 1:1 и не размер группы у сеанса. Пять покрывает и пару с зашедшим
 * родителем, и первые групповые занятия, а разводить два числа значит развести
 * и два пути отладки «почему шестого не пустило».
 *
 * ⚠️ **Определение ОДНО, и это главное свойство, а не само число.** Владелец
 * назвал направление: вместимость станет свойством преподавателя (уровень
 * подписки), то есть сегодняшняя константа завтра станет функцией от него.
 * Поэтому:
 *
 * - число живёт здесь, в общем пакете, и нигде больше;
 * - API передаёт его при создании комнаты (`ensureRoom`), и для КОНКРЕТНОЙ
 *   комнаты источник истины это она, а не эта константа;
 * - клиент получает его ОТ СЕРВЕРА тем же ответом, что и пропуск в комнату
 *   (`TokenResponse.capacity`), и своего литерала не держит.
 *
 * ⚠️ **В `infra/roles/media/livekit.yaml.tmpl` стоит НЕ это число, а системный
 * предел** (двадцать): продуктовая вместимость живёт здесь, а конфиг
 * медиасервера только не даёт комнате вырасти в что-то неожиданное.
 */
export const ROOM_PARTICIPANTS_MAX = 5;

export const LessonStatus = z.enum(["created", "live", "ended"]);
export type LessonStatus = z.infer<typeof LessonStatus>;

export const CreateLessonRequest = z.object({
  title: z.string().min(1).max(200),
  /**
   * Кому урок. Опционально в expand-релизе: старый бандл этого поля не знает
   * и создаёт урок без ученика. Contract сделает его обязательным.
   */
  studentUserId: z.string().uuid().optional(),
  /**
   * Заготовка, с которой начинается урок. Её слайды КОПИРУЮТСЯ в Y.Doc урока
   * при создании, ссылки на заготовку у урока не остаётся: иначе правка
   * материала переписала бы прошлые занятия.
   *
   * Схема id повторена здесь строкой, а не взята из plans.ts, ради отсутствия
   * цикла импортов: plans.ts тянет board.ts, а тот этот файл.
   */
  planId: z.string().uuid().optional(),
});
export type CreateLessonRequest = z.infer<typeof CreateLessonRequest>;

/**
 * Занятие группы в строке списка: как называется и на скольких человек.
 *
 * ⚠️ **Поле есть только у СВЁРНУТОЙ строки** (архив, список уроков, список
 * уроков заготовки): одно занятие группы это N уроков, и списки схлопывают их в
 * одну строку (2.3 `docs/group-room-design.md`). У карточки одного урока поля
 * нет вовсе, потому что там показывать группу незачем.
 *
 * ⚠️ **Поля НЕТ в ответе ученику**, как и `planTitle`: он не отличает групповое
 * занятие от индивидуального, и в своём архиве видит СВОЙ урок.
 *
 * ⚠️ **Идентификатор нужен НЕ ради ссылки, а ради счёта.** До 02.09.2026 его
 * здесь не было («со строки списка никуда не ведут»), и шапка экрана «Уроки по
 * этой заготовке» сливала занятия одной группы по НАЗВАНИЮ. Это ровно та сшивка
 * по подписи, которую проект отверг трижды рядом (`presence-stamp.ts`,
 * `roster.ts`, `participant-rows.ts`): название выбирает человек, и «9Б» бывает
 * и по понедельникам, и по четвергам, и за прошлый год. В сетке расписания
 * идентификатор стоит по своей причине, там из карточки открывают расписание
 * группы, а здесь по этой.
 */
export const LessonGroup = z.object({
  /**
   * ⚠️ Необязательный, как соседние поля этой пачки (`Lesson.kind`,
   * `Lesson.group`): новый бандл против старого API не имеет права уронить
   * разбор всего списка из-за поля, которого тот не знает.
   */
  id: z.string().uuid().optional(),
  title: z.string(),
  /** Сколько уроков свернулось в эту строку, то есть на скольких занятие шло. */
  size: z.number().int().positive(),
});
export type LessonGroup = z.infer<typeof LessonGroup>;

/**
 * Урок принадлежит паре, а не человеку (раздел 5.10 CLAUDE.md).
 *
 * Поля сверх трёх старых опциональны, и это не лень: в открытой вкладке
 * ученика крутится старый бандл, чья zod-схема о них не знает. Expand-релиз
 * обязан отдавать ответ, который разбирают обе версии.
 */
export const Lesson = z.object({
  id: LessonId,
  title: z.string(),
  createdAt: z.number().int(),

  status: LessonStatus.optional(),
  startedAt: z.number().int().nullable().optional(),
  endedAt: z.number().int().nullable().optional(),
  teacherUserId: z.string().uuid().optional(),
  studentUserId: z.string().uuid().nullable().optional(),
  studentDisplayName: z.string().nullable().optional(),
  /** Роль вычисляется сервером из сессии, а не приходит от клиента. */
  myRole: ParticipantRole.optional(),
  /** Есть ли снимок доски: без него архив показывать нечего. */
  hasBoardSnapshot: z.boolean().optional(),
  /**
   * Заготовка, из которой собран урок. `null` у урока без заготовки И у урока,
   * чью заготовку физически стёрли: связь слабая намеренно, урок несёт копию
   * слайдов и обязан пережить исчезновение источника.
   */
  planId: z.string().uuid().nullable().optional(),
  /**
   * Когда урок взял материал заготовки. `null` означает «ещё не взял», то есть
   * правки заготовки до сих пор доезжают до него сами.
   *
   * ⚠️ У всех уроков, созданных до появления связи, здесь `null` при `planId`
   * тоже `null`, и это не «не взял»: у них материал в документе с самого
   * создания. Различать надо по паре, а не по одному полю.
   */
  planMaterializedAt: z.number().int().nullable().optional(),
  /**
   * Название заготовки, из которой собран урок.
   *
   * ⚠️ Заполняется НЕ везде, а только там, где экран его показывает (список
   * архива), и только преподавателю ЭТОГО урока. Заготовка это его материал:
   * ученику она не адресуема вовсе, все ручки заготовок отвечают ему 404, и
   * поле, которое однажды станет ссылкой, не должно доезжать до того, кому по
   * этой ссылке откажут. Отсутствие поля и `null` означают разное: первое это
   * «не спрашивали», второе это «заготовки нет или её стёрли».
   */
  planTitle: z.string().nullable().optional(),
  /**
   * Занятие расписания, которым был этот урок: слот и ИСХОДНАЯ локальная дата.
   *
   * ⚠️ `occursOn` при переносе не меняется: урок носит идентичность занятия, а
   * не его текущее место. Переезжает только `scheduledAt`. Оба `null` у ручных
   * уроков и у всех, созданных до расписания.
   */
  slotId: z.string().uuid().nullable().optional(),
  occursOn: z.string().nullable().optional(),
  /**
   * Момент занятия, зафиксированный при создании урока.
   *
   * ⚠️ Это СЛЕПОК, а не источник истины для будущего. Между «подготовить» и
   * занятием момент может уехать переносом, правкой слота, сменой зоны серии
   * или обновлением tzdata, поэтому состояние занятия и окно входа считаются
   * от раскрытия. Для прошлого наоборот: занятие состоялось в конкретный
   * момент, и правка расписания задним числом его не двигает.
   *
   * ⚠️ У подавляющего большинства уроков в базе здесь `null`. Подставлять
   * вместо него `createdAt` запрещено: это выдуманные данные.
   */
  scheduledAt: z.number().int().nullable().optional(),
  /**
   * Момент, когда занятие кончается ПО РАСПИСАНИЮ.
   *
   * Считает его сервер одним выражением (`occurrenceEndsAtSql` в
   * `repo/lessons.ts`): длительность разрешается тем же порядком, что в
   * календаре (правка занятия, слот, серия, своя длительность разового урока), и
   * прибавляется к `scheduledAt`. Клиент её не складывает сам ни в одном месте:
   * четыре источника длительности это четыре места, где однажды забудут пятый.
   *
   * ⚠️ **`null` означает «конца не существует», и выдумывать его нельзя.** Так
   * отвечает ручной урок из раздела «Уроки»: времени у него нет вовсе, значит
   * нет и конца. Подставить час по умолчанию значило бы нарисовать полосу,
   * которая показывает придуманное.
   *
   * ⚠️ **Отсутствие поля и `null` означают разное**, как у `planTitle` и
   * `groupSessionId` выше. Отсутствие это «не спрашивали»: поле едет с выборкой
   * ОДНОГО урока (`getLessonView`, а это карточка урока, восстановление из
   * корзины, завершение занятия, заведение разового, вход в занятие и правка
   * внешней ссылки) и ни в одном СПИСКЕ, потому что конца занятия списки не
   * показывают.
   *
   * ⚠️ Едет ОБЕИМ сторонам, как `scheduledAt` строкой выше, и это не пропуск
   * правила видимости, а его отсутствие: длительность своего занятия ученик и
   * так видит в сетке расписания (`ScheduleEntry.durationMin`). Кто ВИДИТ из
   * него часы и полосу остатка, решает клиент: решением владельца 29.09.2026
   * это инструмент преподавателя, и ученику он не рисуется вовсе.
   */
  scheduledEndsAt: z.number().int().nullable().optional(),
  /**
   * Момент, в который СЕРВЕР собрал этот ответ.
   *
   * ⚠️ **Нужен ровно одному: поправке на разошедшиеся часы.** Конец занятия
   * посчитан по часам сервера, а остаток от него считает браузер по часам
   * устройства, и домашний ноутбук, спешащий на десять минут, показал бы пустую
   * полосу и «занятие закончилось» за десять минут до конца. Противоречия при
   * этом не видно: цифры часов показывают «правильное» по его же компьютеру.
   * Клиент считает разницу ОДИН раз, в момент получения ответа, и применяет её
   * ТОЛЬКО к остатку: часы на экране обязаны остаться часами устройства, потому
   * что рядом с ними стоят системные часы того же устройства.
   *
   * ⚠️ Едет ВМЕСТЕ с `scheduledEndsAt` и только с ним: порознь они бесполезны, а
   * поправка без конца занятия ничего не поправляет. Поле необязательное,
   * правило expand: старый бандл о нём не знает, а без него клиент считает
   * поправку нулевой, то есть ведёт себя ровно как до этой правки.
   *
   * ⚠️ Часами сервера это поле не делает никого: сверять по нему время суток на
   * экране нельзя, задержка сети в него не заложена.
   */
  serverNow: z.number().int().optional(),
  /**
   * Ссылка на созвон в чужом сервисе (Zoom, Телемост, Meet), УЖЕ РАЗРЕШЁННАЯ
   * сервером: своя ссылка урока, иначе ссылка его серии, иначе `null`. Правило
   * и его причины в `resolveExternalCallUrl` (`./schedule.ts`).
   *
   * ⚠️ Едет ОБЕИМ сторонам и показывается наравне с кнопкой входа в нашу
   * комнату: режим сосуществования, раздел 7 `docs/switching-costs.md`.
   * Схема здесь нарочно слабая (`z.string()`, а не `ExternalCallUrl`): строгая
   * схема на ответе означала бы 500 на весь список уроков из-за одной строки.
   *
   * Схема повторена здесь строкой, а не взята из `schedule.ts`, по той же
   * причине, что `planId` выше: этот файл не тянет за собой ни одного соседа,
   * и заводить ему первую зависимость ради `z.string()` незачем.
   */
  externalCallUrl: z.string().nullable().optional(),
  /**
   * Ссылка принадлежит самому уроку, а не унаследована от серии. Нужно форме
   * правки: заполнив поле унаследованной ссылкой, первое же сохранение
   * превратило бы наследование в копию, и правка серии перестала бы доезжать.
   */
  externalCallOwn: z.boolean().optional(),
  /**
   * Сеанс групповой комнаты, в который входит этот урок. `null` это занятие 1:1
   * со своей доской, и таких подавляющее большинство.
   *
   * ⚠️ **Отсутствие поля и `null` означают РАЗНОЕ, и путать их нельзя ни в одном
   * читателе.** Отсутствие это «сервер старше этой правки, мы не спрашивали»,
   * `null` это «сеанса нет». Привычное `?? null` схлопывает первое во второе и
   * бьёт дважды: имя документа уезжает на личную доску вместо общей, а уборщик
   * локальных баз получает законно выглядящий полный список, в котором нет ни
   * одного сеансового имени, и стирает базу с несинхронизированной работой
   * ребёнка (`docs/group-room-design.md`, раздел 9). Правило то же, что у
   * `planTitle` выше, только цена ошибки другая.
   *
   * Из этого поля клиент доски считает имя Y.Doc (`boardIdOf`): у урока с
   * сеансом документ общий и назван сеансом.
   */
  groupSessionId: z.string().uuid().nullable().optional(),
  /**
   * Личное занятие или групповое.
   *
   * ⚠️ **Отдельным полем, а не выводом из наличия `group`**, и довод тот же, что
   * у одноимённого поля строки сетки (`ScheduleEntry.kind`), только случай свой:
   * `group` требует названия группы, за которое платит выборка, а `kind`
   * считается по колонке, лежащей в самой строке урока. Список, не заплативший
   * за название, всё равно обязан отличить занятие группы от личного, иначе
   * нарисует на нём фигуру одного человека.
   *
   * ⚠️ **Поля НЕТ в ответе ученику вовсе**, как у `planTitle` и `group`.
   */
  kind: z.enum(["student", "group"]).optional(),
  /** ⚠️ Поля НЕТ в ответе ученику вовсе, см. `LessonGroup`. */
  group: LessonGroup.optional(),
  /**
   * Как участника подписывают на этом занятии: над курсором на доске и плиткой
   * в звонке.
   *
   * ⚠️ **Считает СЕРВЕР, и это не перенос вычисления с клиента, а требование
   * приватности.** Первая ступень правила это имя внутри группы
   * (`student_group_member.display_name`), которого у клиента нет и быть не
   * должно: это то, как ученика видят остальные, и задаёт его преподаватель.
   * Правило целиком в `shownNameOf` (`./user.ts`), четыре ступени.
   *
   * Поле необязательное: старый сервер его не присылает, и тогда клиент
   * подписывает себя по-старому, `boardNameOf(me)`.
   */
  shownAs: z.string().optional(),
  /**
   * Предмет урока, выбранный преподавателем в комнате (`Subject` из
   * `./subjects.ts`), и предмет пары. По ним комната решает, какие инструменты
   * показать; действующий предмет считает `resolveLessonSubject`.
   *
   * ⚠️ Оба едут ТОЛЬКО преподавателю и только в карточке урока
   * (`GET /v1/lessons/:id`), а не в списках: предмет пары это поле анкеты, а
   * анкета целиком преподавательская (раздел 5.10 CLAUDE.md).
   *
   * Схема слабая (`z.string()`) по той же причине, что у `externalCallUrl`:
   * строгая схема на ответе означала бы 500 из-за одной строки, а этот файл не
   * тянет за собой соседей. `null` у `subject` это «не выбирали».
   */
  subject: z.string().nullable().optional(),
  pairSubject: z.string().nullable().optional(),
  /**
   * Звонок с преподавателями (`docs/teacher-calls-design.md`, 8.4): урок без
   * ученика, в который приходят гости по ссылке. `role` это роль смотрящего в
   * звонке: `host` у ведущего, `guest` у гостя. Поля нет у любого другого
   * урока.
   *
   * ⚠️ **Отдельным объектом, а не третьим значением `kind`**: старая схема с
   * `z.enum(["student", "group"])` уронила бы разбор строки звонка, а с ней
   * весь список уроков. По той же причине поле необязательное.
   *
   * ⚠️ `myRole` у гостя `student` (права гостя это права ученика), и отличать
   * гостя от ученика урока клиент обязан по этому полю, а не по роли.
   */
  call: z
    .object({
      role: z.enum(["host", "guest"]),
      /** Подпись ведущего по `shownNameOf`, только гостю. */
      hostName: z.string().optional(),
    })
    .optional(),
});
export type Lesson = z.infer<typeof Lesson>;

/**
 * Половина раздела «Уроки»: `?part=active` или `?part=ended`.
 *
 * ⚠️ Две половины растут по-разному, и поэтому у них разные правила выдачи.
 * Идущие и созданные это то, с чем работают сейчас: их немного, и они приезжают
 * целиком под потолком `LESSON_ACTIVE_LIMIT`. Завершённые копятся всю жизнь
 * кабинета и приезжают страницами по курсору.
 *
 * Параметра нет вовсе: прежний ответ (обе половины вперемешку под тем же
 * потолком). Нужен вкладке со старым бандлом, web едет отдельно от API.
 */
export const LessonListPart = z.enum(["active", "ended"]);
export type LessonListPart = z.infer<typeof LessonListPart>;

/**
 * Потолок половины `active` и ответа без `part`, в УРОКАХ, а не в строках.
 *
 * Выборка берёт на один урок больше и по нему ставит `truncated`: молчаливый
 * потолок выглядел как полный список, и по такому списку уборщик локальных баз
 * доски стирал базы уроков, не попавших в ответ.
 */
export const LESSON_ACTIVE_LIMIT = 200;

/**
 * Страница половины `ended`, в уроках. Тот же довод, что у
 * `HOMEWORK_PAGE_LIMIT_DEFAULT`: ответ обязан пролезать под отсечку 16 КБ.
 * `limit` вне границ это `invalid_query`, а не молчаливое усечение.
 */
export const LESSON_ENDED_PAGE_LIMIT_DEFAULT = 20;
export const LESSON_ENDED_PAGE_LIMIT_MAX = 50;

export const LessonListResponse = z.object({
  lessons: z.array(Lesson),
  /**
   * Выборка упёрлась в `LESSON_ACTIVE_LIMIT`: показаны не все. Едет у `active`
   * и у ответа без `part`. По такому списку нельзя судить о полноте, и уборка
   * локальных баз по нему не делается.
   */
  truncated: z.boolean().optional(),
  /** Курсор следующей страницы `ended`; `null` значит «дальше ничего нет». */
  nextCursor: z.string().nullable().optional(),
  /**
   * Сколько СТРОК у завершённых всего, то есть занятие группы считается одним,
   * как и в списке. Едет только с первой страницей `ended`.
   */
  endedCount: z.number().int().optional(),
});
export type LessonListResponse = z.infer<typeof LessonListResponse>;

/**
 * Потолок выборки архива.
 *
 * Живёт в общем пакете, потому что число читают оба: сервер режет им выборку,
 * клиент называет его словами («показаны последние 200»). Разъехавшись, они
 * дали бы подпись, которая врёт про длину списка.
 */
export const ARCHIVE_LIST_LIMIT = 200;

/**
 * Фильтры архива. Оба необязательны, оба применяет БАЗА.
 *
 * Отбор на клиенте здесь запрещён по той же причине, что и в уроках по
 * заготовке: у преподавателя с историей их тысячи, и тянуть весь список ради
 * одного поля на канале 128 кбит/с это минуты ожидания (раздел 3.1 CLAUDE.md).
 */
export const ArchiveFilter = z.object({
  studentUserId: z.string().uuid().optional(),
  planId: z.string().uuid().optional(),
});
export type ArchiveFilter = z.infer<typeof ArchiveFilter>;

/** Пункт выпадающего списка фильтра: то, что реально встречается в архиве. */
export const ArchiveFacet = z.object({
  id: z.string().uuid(),
  title: z.string(),
});
export type ArchiveFacet = z.infer<typeof ArchiveFacet>;

/**
 * Раздел «Архив»: проведённые занятия и то, из чего собрать фильтры.
 *
 * Списки фильтров считаются по ВСЕМУ архиву, а не по текущей выборке, и это не
 * мелочь: посчитанные по выборке, они исчезали бы по мере фильтрации, и снять
 * выбранного ученика стало бы нечем.
 *
 * У ученика оба списка пустые всегда. Фильтровать ему нечего: свои занятия он
 * видит все, а заготовка это чужой материал.
 */
export const ArchiveListResponse = z.object({
  lessons: z.array(Lesson),
  students: z.array(ArchiveFacet),
  plans: z.array(ArchiveFacet),
  /** Выборка упёрлась в потолок: показаны последние `ARCHIVE_LIST_LIMIT`. */
  truncated: z.boolean(),
});
export type ArchiveListResponse = z.infer<typeof ArchiveListResponse>;

/**
 * Потолок пачки уроков.
 *
 * Тридцать это не техническое ограничение, а признак опечатки: у частного
 * репетитора столько учеников за один раз не бывает, а пачка, собранная
 * случайным «выделить всё», создаётся молча и разбирается потом руками.
 * Отдельный код отказа (`batch_too_large`) вместо `invalid_body` затем, чтобы
 * человек прочитал число, а не сообщение zod.
 */
export const LESSON_BATCH_MAX = 30;

/**
 * Уроки сразу нескольким ученикам.
 *
 * ⚠️ Это НЕ групповой урок (решение владельца, не пересматривается). Создаётся
 * N обычных уроков, по одному на ученика: у каждого своя доска, свой архив и
 * свои ответы. Ученики друг друга не видят и друг о друге не знают, общей
 * комнаты нет. Пачка это способ не нажимать «создать» пять раз, и только.
 *
 * Сохранённых групп нет намеренно: галочки в списке учеников дают почти всю их
 * ценность без новой таблицы, а заводить схему под то, чего ещё не просили,
 * дороже, чем добавить её потом.
 */
export const CreateLessonBatchRequest = z.object({
  /** Одно название на все уроки пачки: это одно и то же занятие у разных людей. */
  title: z.string().min(1).max(200),
  /**
   * Кому. Порядок сохраняется, дубли сервер схлопывает: галочками их не
   * получить, но клиент бывает и не наш.
   */
  studentUserIds: z.array(z.string().uuid()).min(1),
  planId: z.string().uuid().optional(),
});
export type CreateLessonBatchRequest = z.infer<typeof CreateLessonBatchRequest>;

/**
 * Ученик, которому урок не создался, и почему.
 *
 * Имени здесь нет: клиент только что выбрал этих людей галочками, их имена у
 * него на экране, а лишнее поле в ответе это лишний запрос на сервере.
 */
export const LessonBatchFailure = z.object({
  studentUserId: z.string().uuid(),
  /** Код отказа, а не текст: слова пишет клиент, он же знает, кому показывает. */
  error: z.string(),
});
export type LessonBatchFailure = z.infer<typeof LessonBatchFailure>;

/**
 * Результат пачки. Оба списка всегда есть, и это главное свойство ответа:
 * «создалось три из пяти» человек обязан прочитать поимённо, а не увидеть
 * «что-то пошло не так».
 *
 * Отказы, общие для всей пачки (чужая заготовка, недособранное задание,
 * превышенный потолок), сюда не попадают вовсе: они приходят обычным 4xx, и
 * при них не создаётся ни один урок.
 */
export const CreateLessonBatchResponse = z.object({
  created: z.array(Lesson),
  failed: z.array(LessonBatchFailure),
});
export type CreateLessonBatchResponse = z.infer<typeof CreateLessonBatchResponse>;
